import React, {useContext, useState, useRef, useEffect, Fragment} from 'react';
import { useQueryClient } from 'react-query';
import SystemContext from "../../../SystemContext";
import {useAuth0} from "@auth0/auth0-react";
import {useSchedulingScratchPads} from '../../../services/flightPlanning';
import {useSettings} from '../../../services/settings';
import {useUserSettings, useAddUserSetting, useUpdateUserSetting} from '../../../services/settings';
import RouteView from "../../../controls/RouteView2";
import {IconButton, Tooltip, Button, Icon, TextField } from '@material-ui/core';
import { ArrowBack, ArrowForward, ZoomIn, ZoomOut, AccessTime } from '@material-ui/icons';
import {ComboSelect, DatePicker, FormSelect, MaskedText } from "../../../controls";
import moment from 'moment';
import __ from 'lodash';
import { Nudge } from '../../../icons';
import IconSwitch from '../../../controls/IconSwitch';
import { Stack } from '@mui/material';
import useScratchpadUsers from '../../../services/auth/useScratchPadUsers';
import ApplyConfirmationDialog from './ApplyConfirmation';
import { getApiSitePrefix } from '../../../functions/SiteDefaults';
import TripTooltip from '../../../controls/RouteView2/TripTooltip';
import FilterAircraftDialog from './FilterAircraft';
const AccessControl = require ("../../../AccessControl");

const apiSitePrefix = getApiSitePrefix();

const isBlank = (value) => {
  return __.isEmpty(value) && (!__.isNumber(value) || __.isNaN(value));
}

const TripSidePanel = (props) => {
  const {editing, canChange, canViewDetails, colours, eventBus} = props;
  const [state, setState] = useState({
    showAsSidePanel: true,
    tripData: null,
    closed: false,
    refresh: 0
  });
  const tooltipState = useRef({open: true, uuid: null, selectedScreen: "map"});

  const handleOnClose = () => {
    setState({...state, closed: true});
  }

  useEffect(()=> {
    const handleShowTripDetails = (props) => {
      const {trip} = props;
      if (JSON.stringify(trip) !== JSON.stringify(state.tripData))
        setState({...state, tripData: trip});
    }
    const handleRefreshDetailPanel = () => {
      setState({...state, refresh: state.refresh + 1});
    }

    eventBus.bind("FlightPlan.showTripDetails", handleShowTripDetails);
    eventBus.bind("FlightPlan.refreshDetailPanel", handleRefreshDetailPanel);
    return () => {
      eventBus.unbind("FlightPlan.showTripDetails", handleShowTripDetails);
      eventBus.unbind("FlightPlan.refreshDetailPanel", handleRefreshDetailPanel);
    } 
  }, [eventBus, state]);

  if (state.closed || !state.tripData) {
    return <div style={{fontSize:18, lineHeight: 1.2, height: "100%", textAlign: "center", position: "relative", top: "49%"}}>Please select a flight to<br/>display associated details</div>
  }

  return (
    <TripTooltip
      showAsSidePanel={true}
      toolTipPinned={tooltipState} 
      tripData={state.tripData} 
      onClose={handleOnClose} 
      editing={editing} 
      canChange={canChange} 
      colours={colours} 
      canViewDetails={canViewDetails}
      refresh={state.refresh}
      style={{height: "calc(100vh - 230px)"}}
    />
  );
}

const SearchField = (props) => {
  const [search, setSearch] = useState('');

  const handleClearSearch=()=>{
    setSearch('');
    if (props.onChange) {
      props.onChange('');
    }
  }

  const clearSearch = {endAdornment: <IconButton style={{padding:0}} disabled={isBlank(search)} onClick={handleClearSearch}><Icon fontSize="small">clear</Icon></IconButton>}

  return (
    <>
      <div style={{display:"inline-block"}}>
        <MaskedText
          id={`text_search`}
          key={'text_search'}
          style={{width: 90, marginTop: -5, ...props.style}}
          mask={"a".repeat(10)}
          forceUpperCase={true}
          inputLabelProps={{ shrink: false }}
          fullwidth
          value={search}
          defaultValue=""
          inputProps={{...clearSearch, disableUnderline: true, placeHolder: "Search"}}
          onChange={(newValue)=> {
            if (newValue !== search) {
              setSearch(newValue);
              if (props.onChange) {
                props.onChange(newValue);
              }
            }
          }}
        />
      </div>
      <IconButton 
        style={{display:"inline-block", width:30}} 
        onClick={()=> {
          if (props.onChange) props.onChange(search);
        }}
      >
        <Icon>search</Icon>
      </IconButton>
    </>
  )
}

const FlightPlan = (props) => {
  const { isAuthenticated, user } = useAuth0();
  const canEdit = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Edit SCRATCHPAD", "Apply SCRATCHPAD"]);
  const canApply = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Apply SCRATCHPAD"]);
  const canViewOthers = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "View OTHER SCRATCHPADS"]);
  const canViewDetails = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Basic VIEW"]);
  const canViewUldData = AccessControl.userHasAccess(user, ["Manage ULD STOCK"]);
  //console.log({isAuthenticated, user, canEdit, canApply, canViewOthers})

  const userInfo = AccessControl.getUserInfo();

  const SystemData = useContext(SystemContext);
  const {eventBus, formatChars} = SystemData;
  const queryClient = useQueryClient();
  const classes = props.classes;

  const flags = useRef({
    settingsDone: false,
    loadedSetting: false,
    loadedScratchPadUsers: !canViewDetails,
    loadedScratchPads: !canViewDetails,
  });

  const colours = SystemData.colours;
  const userSetting = useUserSettings({queryClient, extraKeys: ["FLIGHT PLANNING", "LAST VIEW STATE"], callback:()=> {
    flags.current.loadedSetting = true; 
  }});
  const handleAddUserSetting = useAddUserSetting();
  const handleUpdateUserSetting = useUpdateUserSetting();

  if (userSetting.data && Array.isArray(userSetting.data)) {
    if (userSetting.data.length <= 0 && !flags.current.settingsDone) {
      flags.current.settingsDone = true;
      handleAddUserSetting.mutate({extraKeys:['FLIGHT PLANNING', 'LAST VIEW STATE'], record: {
        module: 'FLIGHT PLANNING', 
        area: 'LAST VIEW STATE', 
        values: {
          editing: false,
          nudgeMode: false,
          estimatedMode: false,
          showOriginals: false,
          scratchPad: 0, 
          startDate: null, 
          blockSize: 25, 
        }
      }});
    }
  }

  let scheduledColour = "rgba(31, 216, 21, 0.6)";
  if (colours) {
    const tripColours = __.find(colours, {code: 'tripState'});
    if (tripColours) {
      if (__.has(tripColours.value, 'SCHEDULED')) {
        scheduledColour = tripColours.value['SCHEDULED'];
      }
    }
  } 

  let setting = {
    editing: null,
    nudgeMode: false,
    estimatedMode: false,
    showOriginals: false,
    scratchPadUser: user.sub,
    scratchPad: 0, 
    startDate: null, 
    blockSize: 25, 
    dragTimeUnit: 15,
    detailAsPanel: true,
    hiddenAircraft: [],
    ...(userSetting.data && Array.isArray(userSetting.data) && userSetting.data.length> 0?userSetting.data[0].values:{}),
  }

  const setSetting = (changes) => {
    if (userSetting.data && Array.isArray(userSetting.data)) {
      const oldRecord = userSetting.data[0];
      const oldValues = oldRecord.values;
      const newValues = {...oldValues, ...changes};
      handleUpdateUserSetting.mutate({extraKeys:['FLIGHT PLANNING', 'LAST VIEW STATE'], record: {...oldRecord, values: newValues}, oldRecord});
      setting = {...setting, ...changes};       
    }
  }

  // const [state, setState] = useState({
  //   editing: false,
  //   scratchPadUser: null,
  //   scratchPad: 0,
  //   nudgeMode: false,
  //   estimatedMode: false,
  //   showOriginals: false,
  //   refresh: 0,
  // });

  const scratchPadUsers = useScratchpadUsers({queryClient, callback:()=> {
    flags.current.loadedScratchPadUsers = true; 
    //console.log(flags);
  }});
  //console.log('getting scratchPads ', setting.scratchPadUser);
  const scratchPads = useSchedulingScratchPads({queryClient, extraKeys: setting.scratchPadUser?[setting.scratchPadUser]:[], callback:()=> {
    flags.current.loadedScratchPads = true; 
    //console.log(flags);
  }});

  const now = moment();
  const startPreviousMonth = moment(now).startOf('month').subtract(1,'month').format('YYYY-MM-DD HH:mm:ss');
  const endNextMonth = moment(now).endOf('month').add(1,'month').format('YYYY-MM-DD HH:mm:ss');

  const handleDateChange = (newDate) => {

  } 

  const handleNowClick = () => {
    const currentTime = moment().format('YYYY-MM-DD HH:mm:ss');
    eventBus.trigger('RouteView.scrollTo', currentTime);
  }

  const handleSelectDate = (newDate) => {
    const currentTime = moment(newDate).format('YYYY-MM-DD');
    eventBus.trigger('RouteView.scrollTo', `${currentTime} 12:00:00`);
  }

  const handleSearchChanged = (searchText) => {
    eventBus.trigger('RouteView.search', searchText);
  }

  const handleSearchNext = (searchText) => {
    eventBus.trigger('RouteView.search', searchText);
  }

  const handleForwardClick = () => {
    eventBus.trigger('RouteView.scrollForward');
  }

  const handleBackwardClick = () => {
    eventBus.trigger('RouteView.scrollBackward');
  }

  const handleZoomInClick = () => {
    eventBus.trigger('RouteView.zoomIn');
  }

  const handleZoomOutClick = () => {
    eventBus.trigger('RouteView.zoomOut');
  }

  const handleRefreshClick = () => {
    eventBus.trigger('RouteView.refresh');
  }

  const handleEditStateChanged = (event, value) => {
    if (!value) {
      setSetting({editing: value, scratchPadHasChanges: false});
    } else {
      setSetting({editing: value});
    }
  }

  const handleScratchPadChanged = (event, change) => {
    const value = change.props.value;
    setSetting({scratchPad: value});
  }

  const handleScratchPadUserChanged = (event, change) => {
    const value = change.props.value;
    setSetting({scratchPadUser: value, scratchPad: 0});
  }

  const handleNudgeModeChanged = () => {
    const value = !setting.nudgeMode;
    setSetting({nudgeMode: value});
  }

  const handleEstimatedModeChanged = () => {
    const value = !setting.estimatedMode;
    setSetting({estimatedMode: value});
  }

  const handleShowDetailsPanelChanged = () => {
    const value = !setting.detailAsPanel;
    setSetting({detailAsPanel: value});
    setTimeout(()=> {
      eventBus.trigger('RouteView.repaint');
    }, 5);
  }

  const handleShowOriginalsChanged = () => {
    const value = !setting.showOriginals;
    setSetting({showOriginals: value});
  }

  const handleDragTimeUnitChanged = (value) => {
    setSetting({dragTimeUnit: value});
  }
  const handleAddTrip = () => {
    eventBus.trigger('RouteView.addTrip');
  }

  const handleScratchPadReset = () => {
    SystemData.confirm(
      `Reset Scratch Pad`,
      `Are you sure you wish to reset the Scratch Pad'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      (response) => {
        if (response) {
          eventBus.trigger('RouteView.resetScratchPad');
        }
      }
    );
  }

  const handleFilterAircraft = () => {
    eventBus.trigger("FilterAircraftDialog.open", {record: {hiddenAircraft: (setting.hiddenAircraft||[]).join(',')}});
  }
  const handleAircraftFilterChanged = (params) => {
    debugger;
    if (!params) return;
    if (!params.record) return;
    debugger;
    if (!params.record.hiddenAircraft || (params.record.hiddenAircraft||'').trim().length === 0) {
      setSetting({hiddenAircraft: []});
    } else {
      setSetting({hiddenAircraft: [...params.record?.hiddenAircraft.split(',')]});
    }
  }
  

  const handleApplyScratchPad = () => {
    eventBus.trigger("ApplyConfirmationDialog.open", {scratchPadUser: setting.scratchPadUser, scratchPad: setting.scratchPad, canSave: (canApply && canChange)});
  }

  const scratchPadUserOptions = scratchPadUsers.data?scratchPadUsers.data.map((scratchPadUser, i) => {
    return {code: scratchPadUser.userId, value: scratchPadUser.name};
  }).sort((u1, u2) => {
    if (u1.code === user.sub) return -1;
    if (u2.code === user.sub) return 1;
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const scratchPadOptions = scratchPads.data?scratchPads.data.map((scratchPad, i) => {
    return {code: i, value: scratchPad.scratchPadName||`Scratch Pad ${i + 1}`};
  }):null;

  const canChange = canEdit && (!setting.scratchPadUser || setting.scratchPadUser === user.sub);

  const scratchPadHasChanges = setting.editing && scratchPads.data && scratchPads.data[setting.scratchPad||0] && scratchPads.data[setting.scratchPad||0].deltas.hasChanges;
  //console.log('planning');
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <div style={{display:"block", border: "1px solid #909090", backgroundColor: setting.editing?canChange?"#ffff00":"#dddddd":"#ffffff", color: "#000000"}}>
          <div style={{display:"inline-block", width: "calc(50% - 345px)", textAlign: "left"}}>
            <div style={{display: "inline-block"}}>
              <IconSwitch icons={['visibility', 'edit']} backgroundColors={[canEdit?"#000000":"#999999","#ee0000"]} disabled={!canEdit} onChange={handleEditStateChanged} checked={setting.editing}/>
            </div>
            {setting.editing?(
              <>
                {canViewOthers?
                <div style={{paddingLeft: 20, display: "inline-block"}}>                
                  <FormSelect 
                    id="selectScratchPadUser"
                    options={scratchPadUserOptions}
                    readOnly={!setting.editing}
                    onChange={handleScratchPadUserChanged}
                    value={setting.scratchPadUser||user.sub}
                    style={{width: 200}}
                  />
                </div>
                :null}
                <div style={{paddingLeft: 20, display: "inline-block"}}>                
                  <FormSelect 
                    id="selectScratchPad"
                    options={scratchPadOptions}
                    readOnly={!setting.editing}
                    onChange={handleScratchPadChanged}
                    value={setting.scratchPad||0}
                  />
                </div>
                <div style={{display: "inline-block"}}>
                  <Tooltip title="Reset Scratch Pad">
                    <IconButton style={{padding:2}} disabled={!scratchPadHasChanges || !canChange} onClick={handleScratchPadReset}><Icon>restore</Icon></IconButton>
                  </Tooltip>
                </div>
                {canApply && canChange?
                <div style={{display: "inline-block", paddingLeft: 10}}>
                  <Tooltip title="Apply Scratch Pad Changes">
                    <Button variant="contained" disabled={!scratchPadHasChanges || !canChange || !canApply} color="secondary" startIcon={(<Icon>check_circle</Icon>)} style={{paddingLeft: 10, paddingRight: 10, paddingTop:2, paddingBottom: 2}} onClick={handleApplyScratchPad}>Apply</Button>
                  </Tooltip>
                </div>
                :canApply?
                <div style={{display: "inline-block", paddingLeft: 10}}>
                  <Tooltip title="View Scratch Pad Changes">
                    <Button variant="contained" disabled={!scratchPadHasChanges} color="secondary" startIcon={(<Icon>preview</Icon>)} style={{paddingLeft: 10, paddingRight: 10, paddingTop:2, paddingBottom: 2}} onClick={handleApplyScratchPad}>View</Button>
                  </Tooltip>
                </div>
                :null}
              </>
            ):null}
            &nbsp;
          </div>
          <Tooltip title={"Aircraft Filter"}>
            <div style={{display: "inline-block", marginRight: 20, border: "1px solid #000000", backgroundColor: Array.isArray(setting.hiddenAircraft) && setting.hiddenAircraft.length?"#ee0000":"#ffffff"}}>
              <IconButton onClick={handleFilterAircraft} style={{padding: 2}}><Icon style={{stroke: "#000000", fill: "#000000"}}>flight</Icon></IconButton>
            </div>
          </Tooltip>
          <div style={{display:"inline-block", width: 120}}>
            <SearchField onChange={handleSearchChanged} style={{display: "inline-block"}}/>
          </div>         
          {/* <div style={{display:"inline-block", width: 120}}>&nbsp;</div> */}
          <div style={{display:"inline-block", width: 100}}>&nbsp;</div>
          <div style={{display:"inline-block", width: 100}}><IconButton style={{display:"inline-block", width:30}}><ArrowBack onClick={handleBackwardClick}/></IconButton><div style={{display:"inline-block", width:40}} onClick={handleNowClick}>Now</div><IconButton style={{display:"inline-block", width:30}}><ArrowForward onClick={handleForwardClick}/></IconButton></div>
          <div style={{display:"inline-block", width: 100}}><IconButton style={{display:"inline-block"}}><ZoomIn onClick={handleZoomInClick}/></IconButton><IconButton style={{display:"inline-block"}}><ZoomOut onClick={handleZoomOutClick}/></IconButton></div>
          <div style={{display:"inline-block", width: 120}}>
            <DatePicker
              onChange={handleSelectDate}
              autoOk={true}
              style={{marginTop: -5}}
              InputProps={{
                disableUnderline: true,
               }}
            />
          </div>
          <div style={{display:"inline-block", width: 100}}><IconButton style={{display:"inline-block"}} onClick={handleRefreshClick}><Icon>refresh</Icon></IconButton></div>
          <div style={{display:"inline-block", width: "calc(50% - 350px)", textAlign: "right"}}>
          {setting.editing?(
            <div style={{display:"block", textAlign: "right", width:"100%"}}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="Drag to nearest time">
                  <div style={{display: "inline-block", paddingRight: 20}}>
                    <ComboSelect 
                      group="FLIGHT MANAGEMENT"
                      subGroup="DRAG TIME UNIT"
                      value={setting.dragTimeUnit}
                      onChange={handleDragTimeUnitChanged}
                    />
                  </div>
                </Tooltip>
                {/* <Tooltip title={setting.estimatedMode?"Change Estimated Times":"Change Scheduled Times"}>
                  <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: setting.estimatedMode?estimatedColour:scheduledColour}}>
                    <IconButton onClick={handleEstimatedModeChanged} style={{padding: 2}}><AccessTime style={{stroke: "#000000", fill: "#000000"}}></AccessTime></IconButton>
                  </div>
                </Tooltip> */}
                <Tooltip title="Nudge Mode">
                  <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: canChange?setting.nudgeMode?'rgb(233, 83, 0)':'rgb(255,255,255)':'rgb(200,200,200)'}}>
                    <IconButton onClick={handleNudgeModeChanged} style={{padding: 2}} disabled={!canChange}><Nudge style={canChange?{stroke: "#000000", fill: "#000000"}:{stroke: "#999999", fill: "#999999"}}></Nudge></IconButton>
                  </div>
                </Tooltip>
                <div style={{paddingLeft:10}}>
                  <Tooltip title="Add Trip">
                    <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: canChange?'rgb(255,255,255)':'rgb(200,200,200)'}}>
                        <IconButton onClick={handleAddTrip} style={{padding: 2}} disabled={!canChange}><Icon style={{stroke: "#000000", fill: "#000000"}}>add</Icon></IconButton>
                      </div>
                  </Tooltip>
                </div>
                <Tooltip title={setting.detailAsPanel?"Hide Details Panel":"Show Details Panel"}>
                  <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: setting.detailAsPanel?"#ffffff":"#eeeeee"}}>
                    <IconButton onClick={handleShowDetailsPanelChanged} style={{padding: 2}}><Icon style={{stroke: "#000000", fill: "#000000"}}>{setting.detailAsPanel?"switch_left":"switch_right"}</Icon></IconButton>
                  </div>
                </Tooltip>
              </Stack>
            </div>
          ):(
            <div style={{display:"block", textAlign: "right", width:"100%"}}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title={setting.showOriginals?"Show Original Scheduled Times":"Hide Original Scheduled Times"}>
                  <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: setting.showOriginals?scheduledColour:"#ffffff"}}>
                    <IconButton onClick={handleShowOriginalsChanged} style={{padding: 2}}><Icon style={{stroke: "#000000", fill: "#000000"}}>short_text</Icon></IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={setting.detailAsPanel?"Hide Details Panel":"Show Details Panel"}>
                  <div style={{display: "inline-block", border: "1px solid #000000", backgroundColor: setting.showOriginals?scheduledColour:"#ffffff"}}>
                    <IconButton onClick={handleShowDetailsPanelChanged} style={{padding: 2}}><Icon style={{stroke: "#000000", fill: "#000000"}}>{setting.detailAsPanel?"switch_left":"switch_right"}</Icon></IconButton>
                  </div>
                </Tooltip>
              </Stack>
            </div>
          )}
          </div>
        </div>
        <div style={{marginLeft: 0, height: "calc(100% - 45px)"}}>
          <div style={{width: setting.detailAsPanel===true?"calc(100% - 365px)":"100%", display: "inline-block", height: "100%", verticalAlign: "top"}}>
            {flags.current.loadedSetting && flags.current.loadedScratchPadUsers && flags.current.loadedScratchPads && setting && userSetting && userSetting.data && Array.isArray(userSetting.data) && userSetting.data.length > 0 && colours?(
            <RouteView
              scrollto={now.format('YYYY-MM-DD HH:mm:ss')}
              onChange={handleDateChange}
              startDate={startPreviousMonth}//'2021-01-01 00:00:00'
              endDate={endNextMonth}//'2021-12-31 23:59:59'
              editing={setting.editing}
              canApply={canApply}
              canChange={canChange}
              scratchPadUser={setting.scratchPadUser}
              scratchPad={setting.scratchPad}
              nudgeMode={setting.nudgeMode}
              estimatedMode={setting.estimatedMode}
              showOriginals={setting.showOriginals}
              colours={colours}
              classes={classes}
              userSetting={userSetting.data[0]}
              blockSize={setting.blockSize}
              dragTimeUnit={setting.dragTimeUnit}
              canViewDetails={canViewDetails?true:canViewUldData?'ULD':false}
              detailAsPanel={setting.detailAsPanel}
              userClass = {userInfo?.userClass}
              hiddenAircraft = {setting.hiddenAircraft}
            />): null}
          </div>
          {setting.detailAsPanel===true? 
            <div className="DetailPanel" style={{width: 365, minWidth: 365, display: "inline-block", height: "100%", verticalAlign: "top", maxHeight: "calc(100% - 0px)"}}>
              <div className="MuiTooltip-tooltip" style={{width: 350, minWidth: 350, height: "100%", textAlign: "left", zIndex: 20, position: "relative"}}>
                <TripSidePanel 
                  editing={setting.editing} 
                  canChange={canChange} 
                  colours={colours} 
                  canViewDetails={canViewDetails?true:canViewUldData?'ULD':false}
                  eventBus={eventBus}
                />
              </div>
            </div>:
            <></>
          }
        </div>
        <ApplyConfirmationDialog 
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          formatChars={formatChars}
          classes={classes}
          canEdit={true}
          colours={colours}
          afterSave={()=>{eventBus.trigger("RouteView.refresh")}}
        />
        <FilterAircraftDialog 
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          record={{hiddenAircraft: setting.hiddenAircraft.join(',')}}
          onChange={handleAircraftFilterChanged}
        />
      </div>
  );

}

export default FlightPlan;
